'use client';

import Image from 'next/image';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../../../../core/Tracking/Amplitude/Amplitude';
import { IconImage } from '../../../../../../design-system/components/icons/Image';
import { RATIO } from '../../../../../../design-system/components/ratio/Ratio';
import { Typography } from '../../../../../../design-system/components/typography/Typography';
import { imageLoaderForPicture } from '../../../../../../domains/Layout/Image/URL/ImageURL';
import { Destination } from '../../../../../../resources/destination/domain/types/destination';
import { UgcHighlight } from '../../../../../../resources/ugcHighlight/domain/types/ugcHighlight';
import { useUrlGenerator } from '../../../../../../shared/hooks/useUrlGenerator';
import { PickDomain } from '../../../../../../shared/utilities/types/PickDomain';
import { HomeIconButton } from '../../../IconButton/IconButton';
import { TravelerUgcModal } from '../Modal/TravelerUgcModal';

import style from './TravelerUgcCard.module.css';

type TravelerUgcCardProps = {
    travelerUgc: PickDomain<
        UgcHighlight,
        'id' | 'segment' | 'username' | 'description' | 'pictures',
        {
            destination: PickDomain<Destination, 'id' | 'name' | 'slug' | 'preposition'>;
        }
    >;
    index: number;
};
export function TravelerUgcCard({ travelerUgc, index }: TravelerUgcCardProps) {
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();

    const [isHovered, setIsHovered] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleImageClick = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.ugcClicked({
                ...defaultProperties,
                destination_id: travelerUgc.destination.id,
                destination_slug: travelerUgc.destination.slug,
                block: 'ugc',
                card_number: index + 1,
                is_clickable: travelerUgc.pictures.length > 0,
            });
        });

        setIsModalOpen(true);
    };

    const handleTagClicked = (tagType: 'segment' | 'destination') => {
        trackEvent((ampli, defaultProperties) => {
            ampli.tagClicked({
                ...defaultProperties,
                block: 'ugc',
                card_number: index + 1,
                tag_type: tagType,
            });
        });
    };

    return (
        <div className={style.card}>
            <div className={style.contentWrapper}>
                <Typography className={style.title} scale="secondary-xxs-regular">
                    {travelerUgc.username}
                </Typography>

                <div className={style.description}>{travelerUgc.description}</div>

                <div className={style.cardFooter}>
                    <Typography
                        as="a"
                        href={generatePath('destination', {
                            destinationSlug: travelerUgc.destination.slug,
                        })}
                        className={style.cardTag}
                        scale="primary-xxs-bold"
                        onClick={() => handleTagClicked('destination')}
                    >
                        {travelerUgc.destination.name}
                    </Typography>

                    <Typography
                        className={style.cardTag}
                        scale="primary-xxs-bold"
                        onClick={() => handleTagClicked('segment')}
                    >
                        {formatMessage({
                            id: `global.ugc.segment.${travelerUgc.segment}.label`,
                        })}
                    </Typography>
                </div>
            </div>

            <div className={style.separator} />

            <div
                className={style.imageWrapper}
                onClick={handleImageClick}
                onMouseEnter={() => {
                    setIsHovered(true);
                }}
                onMouseLeave={() => {
                    setIsHovered(false);
                }}
                onFocus={() => {
                    setIsHovered(true);
                }}
                onBlur={() => {
                    setIsHovered(false);
                }}
            >
                {travelerUgc.pictures.slice(0, 1).map((picture) => (
                    <Image
                        className={style.image}
                        key={picture.id}
                        src={picture.id}
                        alt={picture.legend}
                        fill
                        loader={imageLoaderForPicture(picture, {
                            aspectRatio: RATIO['5/8'],
                        })}
                        sizes="300px"
                        loading="lazy"
                    />
                ))}

                <div>
                    <HomeIconButton
                        isHovered={isHovered}
                        className={style.seeImagesButton}
                        Icon={IconImage}
                        onClick={handleImageClick}
                        color="var(--color-neutral-0)"
                    />
                </div>
            </div>

            {isModalOpen ? (
                <TravelerUgcModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    travelerUgc={travelerUgc}
                />
            ) : null}
        </div>
    );
}
